import { useState, useEffect, useCallback, useRef } from 'react';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

export const useDocumentPasteDetection = (
	contentId: string,
	userOffline: boolean,
	isEditorEmpty: boolean,
	titleTextAreaRef?: React.RefObject<HTMLTextAreaElement>,
) => {
	const PASTE_CONTEXT_EXTRACTION_DELAY = 100; // Wait for paste to be applied to DOM
	const PASTE_RESET_DELAY = 500; // Used to reset paste detection

	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });
	const [pasteDetected, setPasteDetected] = useState(false);
	const isEditorEmptyRef = useRef(isEditorEmpty);

	useEffect(() => {
		isEditorEmptyRef.current = isEditorEmpty;
	}, [isEditorEmpty]);

	const isFeatureEnabled =
		isAIEnabledForContent &&
		!userOffline &&
		expValEquals('confluence_ai_title_generation_on_paste', 'isEnabled', true);

	const findProseMirrorElement = useCallback((): HTMLElement | null => {
		const filteredProseMirrors = Array.from(document.querySelectorAll('.ProseMirror')).filter(
			(el) => !el.closest('[data-testid="chromeless-editor"]'),
		);
		return filteredProseMirrors[0] as HTMLElement | null;
	}, []);

	const resetPasteDetected = useCallback(() => {
		setPasteDetected(false);
	}, []);

	useEffect(() => {
		if (!isFeatureEnabled) {
			return;
		}

		const proseMirrorElement = findProseMirrorElement();

		if (!proseMirrorElement) {
			return;
		}

		const handlePaste = (event: ClipboardEvent) => {
			const pastedText = event.clipboardData?.getData('text/plain') || '';
			const currentTitle = titleTextAreaRef?.current?.value ?? '';
			const wasEditorEmpty = isEditorEmptyRef.current;

			if (currentTitle.trim() !== '') {
				return;
			}

			if (
				wasEditorEmpty &&
				pastedText.length > expVal('confluence_ai_title_generation_on_paste', 'charLength', 50)
			) {
				setTimeout(() => {
					const extractedContext = proseMirrorElement.textContent || '';
					if (extractedContext.trim().length > 0) {
						setPasteDetected(true);
					}
				}, PASTE_CONTEXT_EXTRACTION_DELAY);
			}
		};

		proseMirrorElement.addEventListener('paste', handlePaste);

		return () => {
			proseMirrorElement.removeEventListener('paste', handlePaste);
		};
	}, [findProseMirrorElement, isFeatureEnabled, titleTextAreaRef]);

	useEffect(() => {
		if (pasteDetected) {
			const timer = setTimeout(() => setPasteDetected(false), PASTE_RESET_DELAY);
			return () => clearTimeout(timer);
		}
	}, [pasteDetected]);

	return {
		pasteDetected,
		resetPasteDetected,
	};
};
